import { memo, useEffect, useState, type ChangeEvent } from 'react';
import Skeleton from '@mui/material/Skeleton';
import Card from '@mui/material/Card';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import { SimplePagination } from 'ui/molecules/Pagination';
import { WebRoutes } from 'consts/enpoints/web';
import { Filters } from 'consts/transactions';
import Resource from 'ui/layouts/Resource';
import { Link, Outlet, useParams } from 'infrastructure/router';
import { compareIds } from 'util/support';
import type { PspKycSetting } from 'features/pspKycSettings/types';
import useFetchPspKycSettings from './useFetchPspKycSettings';
import type { PspListProps } from './types';

const formatScope = ({ MOR, PSP, countryIso2 }: PspKycSetting) => {
    const parts = [
        PSP || 'All PSPs',
        MOR || 'All domains',
        countryIso2 || 'All countries'
    ];
    return parts.join(' · ');
};

const PspList = ({
    title,
    heading,
    links,
    Route,
    action,
    searchable,
    pageSize
}: PspListProps) => {
    const params = useParams();
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    const { getSettingsSlice, isLoading } = useFetchPspKycSettings();
    const onSearchChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
        setSearch(target.value);
    };

    const normalizedSearch = search.trim().toLowerCase();
    const filteredSettings = searchable && normalizedSearch
        ? getSettingsSlice().filter(({ paymentMethodName, PSP, MOR, countryIso2 }: PspKycSetting) =>
            [paymentMethodName, PSP, MOR, countryIso2]
                .some(value => value?.toLowerCase().includes(normalizedSearch)))
        : getSettingsSlice();

    const isPaginated = Boolean(pageSize && pageSize > 0);
    const pageCount = isPaginated
        ? Math.max(1, Math.ceil(filteredSettings.length / (pageSize as number)))
        : 1;
    const visibleSettings = isPaginated
        ? filteredSettings.slice((page - 1) * (pageSize as number), page * (pageSize as number))
        : filteredSettings;

    useEffect(() => {
        setPage(1);
    }, [normalizedSearch]);

    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount]);

    return (
        <Resource
            title={`KYC Settings | ${title}`}
            breadcrumbsProps={{
                heading,
                links: links.length > 0
                    ? [
                        { name: 'KYC Settings', href: WebRoutes.KYCSettings.Index },
                        ...links
                    ]
                    : [],
                action
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(min-content, 252px) 1fr',
                    columnGap: '24px',
                    alignItems: 'flex-start',
                    width: '100%'
                }}
            >
                <Card
                    sx={{
                        boxShadow: theme => theme.shadows[5],
                        width: '16vw',
                        minHeight: '55vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {searchable && (
                        <Box sx={{ p: 2, pb: 1 }}>
                            <TextField
                                fullWidth
                                type='search'
                                size='small'
                                placeholder='Search'
                                value={search}
                                onChange={onSearchChange}
                                disabled={isLoading}
                                inputProps={{ 'data-test-id': 'pspKycSettings.search' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SearchIcon fontSize='small' />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Box>
                    )}
                    <List
                        component='nav'
                        aria-label='psp-kyc-settings'
                        sx={{ flex: 1, overflowY: 'auto' }}
                    >
                        {isLoading && (
                            Array.from({ length: 10 }).map((_, index) => (
                                <ListItem key={index}>
                                    <Skeleton
                                        variant='rounded'
                                        width='100%'
                                        height={50}
                                    />
                                </ListItem>
                            ))
                        )}
                        {!isLoading && filteredSettings.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary='No PSP KYC settings found'
                                    primaryTypographyProps={{
                                        color: 'text.secondary',
                                        align: 'center'
                                    }}
                                />
                            </ListItem>
                        )}
                        {visibleSettings.map((setting: PspKycSetting) => {
                            const { coreId, PSP } = setting;
                            const primary = PSP;
                            const secondary = formatScope(setting);
                            return (
                                <ListItemButton
                                    key={coreId}
                                    component={Link}
                                    to={Route.Setting(coreId)}
                                    selected={compareIds(
                                        String(params[Filters.pspKycSettingId]),
                                        coreId
                                    )}
                                    data-test-id={`pspKycSettings.card.${coreId}`}
                                >
                                    <ListItemText
                                        primary={primary}
                                        primaryTypographyProps={{
                                            noWrap: true,
                                            title: primary,
                                        }}
                                        secondaryTypographyProps={{
                                            noWrap: true,
                                            title: secondary
                                        }}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                    {isPaginated && !isLoading && pageCount > 1 && (
                        <Box sx={{ px: 2, py: 1 }}>
                            <SimplePagination
                                page={page}
                                pageCount={pageCount}
                                onChange={setPage}
                            />
                        </Box>
                    )}
                </Card>
                <Outlet />
            </Box>
        </Resource>
    );
};

export default memo(PspList);
